import { eventoSseSchema, type EventoSse } from "@copi/shared";

import { tokenStore } from "@/lib/api/token-store";

/**
 * Cliente de Server-Sent Events para el frontend (ADR 018).
 *
 * Por qué `fetch` + `ReadableStream` en lugar del `EventSource` nativo:
 *  - El browser `EventSource` no permite custom headers ⇒ no podríamos
 *    pasar `Authorization: Bearer <token>`. Las alternativas estándar son
 *    todas peores (query param queda en logs, cookie requiere endpoint
 *    nuevo, polyfill agrega dep).
 *  - `fetch` con `ReadableStream` es nativo en todos los browsers modernos
 *    y nos permite el mismo header que cualquier otro request.
 *
 * Responsabilidades:
 *  - Mantener una conexión SSE viva hacia `/eventos/stream`.
 *  - Reconectar con backoff exponencial + jitter si la conexión cae.
 *  - Llamar `onRefreshNeeded` si recibe 401 (refresh transparente del token).
 *  - Parsear el formato SSE (`data: <json>\n\n`) y emitir eventos tipados
 *    validados por Zod.
 *  - Limpieza fina en `disconnect()` (abort fetch + cancel reader).
 *
 * Lo que NO hace:
 *  - No conoce TanStack Query. Solo emite eventos al callback. El mapeo a
 *    `invalidateQueries` vive en `useSse` (separation of concerns).
 *  - No persiste eventos. Si la app está en background, los browsers pueden
 *    pausar el JS — al reactivarse, la app refetchea por sus medios (ese
 *    es el patrón "evento = señal, no payload" del ADR 018).
 */

export interface SseConnectionOptions {
  /** Base URL del API (ej. `http://localhost:3001/api/v1`). */
  baseUrl: string;
  /** Callback invocado por cada evento de dominio recibido. */
  onEvento: (evento: EventoSse) => void;
  /** Si recibimos 401, esto debe refrescar el token y devolver el nuevo. */
  onRefreshNeeded?: () => Promise<string | null>;
  /** Override del fetch (útil para tests). */
  fetchImpl?: typeof fetch;
  /** Override del logger (útil para tests). */
  logger?: Pick<Console, "warn" | "error" | "log">;
}

interface BackoffPolicy {
  /** Delay inicial entre reintentos (ms). */
  inicialMs: number;
  /** Tope del delay (ms). */
  maxMs: number;
  /** Factor multiplicativo por intento. */
  factor: number;
  /** Ratio de jitter (0 a 1) — el delay real es `delay * (1 ± jitter)`. */
  jitter: number;
}

const BACKOFF_POR_DEFECTO: BackoffPolicy = {
  inicialMs: 1_000,
  maxMs: 30_000,
  factor: 2,
  jitter: 0.25,
};

/**
 * Cota superior del buffer del parser SSE. Si el server (o un atacante)
 * envía datos sin separador `\n\n`, el buffer no debe crecer sin límite.
 * 1 MB es ~1000× el tamaño típico de un evento — si llegamos a ese límite,
 * algo está mal y reconectamos.
 */
const BUFFER_MAX_BYTES = 1_048_576; // 1 MB

/**
 * Status codes HTTP que tratamos como **fallos permanentes** — no reintentar.
 *  - 403: el usuario perdió permisos (suspendido, rol cambiado).
 *  - 404: el endpoint cambió o no existe (bug de configuración).
 *  - 410: el endpoint fue eliminado deliberadamente (deprecación).
 *
 * 401 se maneja aparte (puede recuperarse con refresh). 5xx y errores de red
 * son transitorios — siguen reintentando con backoff.
 */
const STATUS_PERMANENTES = new Set([403, 404, 410]);

/**
 * Error interno que señaliza un fallo permanente al loop. Hace que el loop
 * marque la conexión como cerrada sin reintentar.
 */
class SseErrorPermanente extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SseErrorPermanente";
  }
}

export class SseConnection {
  private abortController: AbortController | null = null;
  private cerrado = false;
  /** Guard contra `connect()` llamado más de una vez en la misma instancia. */
  private iniciado = false;
  private intentos = 0;
  private timeoutReconexion: ReturnType<typeof setTimeout> | null = null;
  private readonly fetchImpl: typeof fetch;
  private readonly logger: Pick<Console, "warn" | "error" | "log">;

  constructor(private readonly options: SseConnectionOptions) {
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.logger = options.logger ?? console;
  }

  /**
   * Inicia el loop de conexión (no bloqueante — corre en background).
   * Idempotente: llamar más de una vez no arranca loops paralelos.
   */
  connect(): void {
    if (this.cerrado) {
      throw new Error("SseConnection: instancia ya fue desconectada, crea una nueva.");
    }
    if (this.iniciado) {
      this.logger.warn("[SSE] connect() ya fue invocado — segunda llamada ignorada.");
      return;
    }
    this.iniciado = true;
    void this.loop();
  }

  /** Cierra la conexión y detiene la reconexión. Idempotente. */
  disconnect(): void {
    this.cerrado = true;
    if (this.timeoutReconexion) {
      clearTimeout(this.timeoutReconexion);
      this.timeoutReconexion = null;
    }
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  private async loop(): Promise<void> {
    while (!this.cerrado) {
      try {
        await this.conectarUnaVez();
        // Si conectarUnaVez termina sin throw, el server cerró el stream
        // de forma limpia. Lo tratamos como reconexión normal.
        this.logger.log("[SSE] stream cerrado por el server, reconectando…");
      } catch (err) {
        if (this.cerrado) return;
        if (err instanceof SseErrorPermanente) {
          // Fallo permanente (403, 404, 410, 401 sin refresh exitoso). No
          // reintentar — el usuario debe re-loguearse o un dev debe arreglar
          // el deploy. Marcamos cerrado y salimos.
          this.logger.error(`[SSE] fallo permanente — no se reintenta: ${err.message}`);
          this.cerrado = true;
          return;
        }
        this.logger.warn(
          `[SSE] conexión cayó: ${err instanceof Error ? err.message : String(err)}`,
        );
      }

      if (this.cerrado) return;

      // Backoff exponencial + jitter para evitar reconnect storms tras
      // un restart del server.
      const delay = this.calcularBackoff();
      this.intentos += 1;
      await this.dormir(delay);
    }
  }

  private async conectarUnaVez(): Promise<void> {
    const token = tokenStore.get();
    if (!token) {
      throw new Error("SSE: no hay token disponible");
    }

    this.abortController = new AbortController();
    const res = await this.fetchImpl(`${this.options.baseUrl}/eventos/stream`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "text/event-stream",
      },
      credentials: "include",
      signal: this.abortController.signal,
    });

    if (res.status === 401) {
      // Token expirado: intentar refresh y reconectar.
      const nuevo = this.options.onRefreshNeeded ? await this.options.onRefreshNeeded() : null;
      if (!nuevo) {
        // Sin refresh exitoso, no podemos recuperarnos. El usuario debe
        // re-loguearse — no tiene sentido reintentar en bucle.
        throw new SseErrorPermanente("401 sin refresh exitoso");
      }
      // El nuevo token ya quedó en tokenStore por el handler; el throw
      // (transitorio) hace que el loop reintente con el token fresh.
      throw new Error("SSE: 401, token refrescado, reintentando");
    }

    if (STATUS_PERMANENTES.has(res.status)) {
      throw new SseErrorPermanente(`status ${res.status} — sin recuperación posible`);
    }

    if (!res.ok || !res.body) {
      throw new Error(`SSE: respuesta no-OK ${res.status}`);
    }

    // Conexión exitosa: resetear contador de reintentos.
    this.intentos = 0;
    this.logger.log("[SSE] conectado a /eventos/stream");

    const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
    let bufer = "";

    while (!this.cerrado) {
      const { value, done } = await reader.read();
      if (done) return;

      bufer += value;

      // Cota dura: si el server (o atacante) no manda separadores `\n\n`,
      // el buffer no debe crecer sin límite. Reseteamos y forzamos
      // reconexión — algo está mal con el server o el wire.
      if (bufer.length > BUFFER_MAX_BYTES) {
        throw new Error(`SSE: buffer excedió ${BUFFER_MAX_BYTES} bytes sin separador`);
      }

      // Los eventos SSE se separan por `\n\n`. Cada evento puede tener
      // múltiples líneas (`data: ...`, `event: ...`, `id: ...`).
      let separadorIdx = bufer.indexOf("\n\n");
      while (separadorIdx !== -1) {
        const crudo = bufer.slice(0, separadorIdx);
        bufer = bufer.slice(separadorIdx + 2);
        this.procesarMensaje(crudo);
        separadorIdx = bufer.indexOf("\n\n");
      }
    }
  }

  private procesarMensaje(crudo: string): void {
    // El frame puede contener "event: <type>" + "data: <json>" + "id: <x>"
    // separados por \n. Solo nos importa `data` — el `type` viene en el JSON
    // como `evento.type`.
    let dataLinea = "";
    for (const linea of crudo.split("\n")) {
      if (linea.startsWith("data:")) {
        dataLinea += linea.slice(5).trimStart();
      }
      // Ignoramos comentarios (`: ping`) y otras directivas.
    }

    if (!dataLinea) return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(dataLinea);
    } catch (err) {
      this.logger.warn(
        `[SSE] frame no parseable como JSON: ${err instanceof Error ? err.message : String(err)}`,
      );
      return;
    }

    const result = eventoSseSchema.safeParse(parsed);
    if (!result.success) {
      this.logger.warn(`[SSE] evento no válido contra schema:`, result.error.issues);
      return;
    }

    try {
      this.options.onEvento(result.data);
    } catch (err) {
      // No queremos que un bug del consumidor mate el stream.
      this.logger.error(
        `[SSE] handler onEvento lanzó: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  private calcularBackoff(policy: BackoffPolicy = BACKOFF_POR_DEFECTO): number {
    const base = Math.min(policy.inicialMs * Math.pow(policy.factor, this.intentos), policy.maxMs);
    const jitter = base * policy.jitter * (Math.random() * 2 - 1); // ±jitter
    return Math.max(0, Math.round(base + jitter));
  }

  private dormir(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.timeoutReconexion = setTimeout(() => {
        this.timeoutReconexion = null;
        resolve();
      }, ms);
    });
  }
}
